import initializeFavoriteButton from './offer/offer-favorite-button';
import initializeShareButton from '../_02_molecules/components/extendable-offer-button';

/**
 * @var {HTMLElement} header
 */
let header;

/**
 * @var {HTMLElement} stickyHeader
 */
let stickyHeader;

/**
 * @var {HTMLElement} observableClone
 */
let observableClone;

/**
 * Adds and removes a CSS class to the observable clone when the observable disappear or reappears via
 * the top of the user screen, not via the bottom.
 *
 * @param {IntersectionObserverEntry} observable
 */
function intersectionObserverCallback(observable) {
  observableClone.classList.toggle(
    'sticky-header__container--visible',
    !observable.isIntersecting && observable.boundingClientRect.y < stickyHeader.getBoundingClientRect().y,
  );
}

/**
 * Creates an observer in charge of detecting intersection between observable elements and the page body.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API}
 *
 * @returns {IntersectionObserver}
 */
function createObserver() {
  return new IntersectionObserver((observables) => {
    observables.forEach(intersectionObserverCallback); // by design, there should not be more than one entry
  }, {
    rootMargin: `-${header.getBoundingClientRect().height}px 0px 0px 0px`,
  });
}

/**
 * Creates a clone of the observable element and injects it into the sticky header node.
 *
 * The clone is wrapped into a container which will be hidden or displayed jointly
 * to the visibility status of the observable element.
 *
 * @param {HTMLElement} observable
 */
function createStickyClone(observable) {
  observableClone = document.createElement('div');
  observableClone.classList.add('sticky-header__container');
  observableClone.appendChild(observable.cloneNode(true));
  stickyHeader.appendChild(observableClone);

  const favoriteButton = stickyHeader.querySelector('[data-offer-favorite-button="on"]');
  if (favoriteButton) {
    initializeFavoriteButton(favoriteButton);
  }

  const shareButtonContainer = stickyHeader.querySelector('[data-share-button="on"]');
  if (shareButtonContainer) {
    initializeShareButton(shareButtonContainer);
  }
}

/**
 * Creates an observer which will trigger a callback each time an observable
 * element is visible or not, referring the page body.
 *
 * Observable elements are DOM nodes with the data-sticky-header attribute set to "on".
 * By design, only one element can be observed at a time; the first one appearing in the page.
 *
 * Should not be called more than once.
 */
export default function () {
  header = document.getElementById('header');
  stickyHeader = document.getElementById('sticky-header');

  /** @var {HTMLElement|null} observable */
  const observable = document.querySelector('[data-sticky-header="on"]');
  if (!observable) {
    return;
  }

  const observer = createObserver();
  createStickyClone(observable);
  observer.observe(observable);
}
